/**
 * Ask layer, access to the compiled catalog.
 *
 * Part of the page guide's Ask tab (see `frontend/src/help/README.md`); from the
 * user-guide-creator skill, as in MGA.
 * `catalog.data.ts` next to it is GENERATED by `npm run catalog:build` from `frontend/catalog/**` —
 * commit it; the pre-commit hook rebuilds and re-stages it, and `catalog:check` fails when it is
 * stale.
 */
import {
  CATALOG_BASE_LANGUAGE,
  type CatalogEntry,
  type CatalogLanguage,
  type CatalogTranslation,
} from './types';
import { CATALOG_ENTRIES, CATALOG_HASH } from './catalog.data';
import { buildIndex, type CatalogIndex } from './search';

/**
 * Access to the compiled capability catalog.
 *
 * The entries come from a generated module rather than from `catalog/**` on disk: the backend
 * build is a plain `tsc` that copies no non-TS assets, so a filesystem read would work locally
 * and return nothing in the deployed bundle. `npm run catalog:check` fails the build when the
 * generated module falls behind the markdown.
 */

export const catalogEntries = (): readonly CatalogEntry[] => CATALOG_ENTRIES;

export const catalogHash = (): string => CATALOG_HASH;

let index: CatalogIndex | null = null;

/** Built once per process — the catalog cannot change without a deploy. */
export const catalogIndex = (): CatalogIndex => {
  index ??= buildIndex(CATALOG_ENTRIES);
  return index;
};

export interface EntryView {
  job: string;
  description: string;
  prerequisites: string | null;
  /** True when the requested language had no translation and this is the base language. */
  fallback: boolean;
  language: CatalogLanguage;
}

/**
 * The entry as one language. A missing translation falls back to the base language and says so,
 * rather than hiding the entry: a user who reads German would still rather have the English
 * answer than "not found".
 */
export const viewEntry = (entry: CatalogEntry, language: CatalogLanguage): EntryView => {
  // Widened: while English is the only language, `language` narrows to `never` here.
  const translations = entry.i18n as Partial<Record<string, CatalogTranslation>> | undefined;
  const translation = language === CATALOG_BASE_LANGUAGE ? null : translations?.[language];
  if (translation) {
    return {
      job: translation.job,
      description: translation.description,
      prerequisites: translation.prerequisites ?? null,
      fallback: false,
      language,
    };
  }
  return {
    job: entry.job,
    description: entry.description,
    prerequisites: entry.prerequisites ?? null,
    fallback: language !== CATALOG_BASE_LANGUAGE,
    language: CATALOG_BASE_LANGUAGE,
  };
};
